import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Code2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Product } from "@/lib/types";
import { parseJsonObject } from "@/lib/utils";
import { ConfigurationEntitlements } from "./configuration-entitlements";
import { ConfigurationPolicy } from "./configuration-policy";
import { ConfigurationValidation } from "./configuration-value-editor";
import { configurationDraftIsValid } from "./configuration-validation";

export type LicenseConfigurationValidationError = "entitlements_json" | "policy_json" | "max_devices";

export function LicenseConfigurationFields({ product, entitlements, policy, disabled = false, kind = "paid",
  baselinePolicy, validationError = null, onEntitlementsChange, onPolicyChange, onDraftInvalidChange,
}: {
  product?: Product; entitlements: string; policy: string; disabled?: boolean; kind?: string;
  baselinePolicy?: Record<string, unknown>; validationError?: LicenseConfigurationValidationError | null;
  onEntitlementsChange: (value: string) => void; onPolicyChange: (value: string) => void;
  onDraftInvalidChange?: (invalid: boolean) => void;
}) {
  const { t } = useTranslation();
  const [rawMode, setRawMode] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Set<string>>(() => new Set());
  const report = useCallback((id: string, invalid: boolean) => setErrors((current) => {
    if (current.has(id) === invalid) return current;
    const next = new Set(current);
    if (invalid) next.add(id); else next.delete(id);
    return next;
  }), []);
  const parsedEntitlements = useMemo(() => parseObject(entitlements), [entitlements]);
  const parsedPolicy = useMemo(() => parseObject(policy), [policy]);
  const malformed = !parsedEntitlements || !parsedPolicy;
  const invalid = !disabled && (malformed || errors.size > 0);
  useEffect(() => { onDraftInvalidChange?.(invalid); }, [invalid, onDraftInvalidChange]);

  useEffect(() => {
    if (!validationError) return;
    setRawMode(true);
    const targetId = validationError === "entitlements_json" ? "license-entitlements" : "license-policy";
    const frame = requestAnimationFrame(() => document.getElementById(targetId)?.focus());
    return () => cancelAnimationFrame(frame);
  }, [validationError]);

  return <ConfigurationValidation.Provider value={report}>
    <div ref={root} className="space-y-4" data-license-configuration data-invalid={invalid}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold text-ink">{t("licenseConfiguration.title", "Entitlements and policy")}</h2>
        <Button type="button" variant="ghost" size="sm" aria-pressed={rawMode} onClick={() => {
          if (!rawMode && [...errors].some((id) => id.startsWith("draft-"))) { configurationDraftIsValid(root.current?.parentNode ?? document); return; }
          setRawMode((current) => !current);
        }}><Code2 className="size-3.5" aria-hidden="true" />
          {rawMode ? t("licenseConfiguration.useBuilder", "Use builder") : t("licenseConfiguration.rawJson", "Raw JSON")}
        </Button>
      </div>
      {invalid && <p role="alert" tabIndex={-1} className="text-sm text-danger">{malformed
        ? t("licenses.jsonValidationError", "Entitlements and policy must be valid JSON objects.")
        : t("configurationEditor.fixErrors", "Correct the configuration errors before continuing.")}</p>}
      <fieldset disabled={disabled} className="m-0 min-w-0 border-0 p-0">
        {rawMode ? <div className="grid gap-4 lg:grid-cols-2">
          <JsonField id="license-entitlements" label={t("licenseConfiguration.entitlementsJson", "Entitlements JSON")}
            value={entitlements} onChange={onEntitlementsChange} tourId="license-entitlements"
            invalid={!parsedEntitlements || validationError === "entitlements_json"} />
          <JsonField id="license-policy" label={t("licenseConfiguration.policyJson", "Policy JSON")}
            value={policy} onChange={onPolicyChange} tourId="license-device-policy"
            invalid={!parsedPolicy || validationError === "policy_json" || validationError === "max_devices"}
            message={validationError === "max_devices" ? t("licenses.maxDevicesValidationError", "Max devices must be a positive whole number.") : undefined} />
        </div> : <div className="grid items-start gap-4 lg:grid-cols-2">
          <section className="min-w-0 border border-line" data-tour-id="license-entitlements">
            <h3 className="border-b border-line px-4 py-3 text-sm font-medium">{t("licenseConfiguration.featureGrants", "Feature grants")}</h3>
            {parsedEntitlements ? <ConfigurationEntitlements catalog={product?.feature_catalog ?? {}} value={parsedEntitlements}
              onChange={(next) => onEntitlementsChange(JSON.stringify(next, null, 2))} />
              : <p className="px-4 py-3 text-xs text-danger">{t("licenseConfiguration.entitlementsJsonInvalid", "Entitlements JSON is invalid. Open Raw JSON to correct it.")}</p>}
          </section>
          <section className="min-w-0 border border-line" data-tour-id="license-device-policy">
            <h3 className="border-b border-line px-4 py-3 text-sm font-medium">{t("configurationEditor.policy", "Policy")}</h3>
            {parsedPolicy ? <ConfigurationPolicy value={parsedPolicy} productPlatforms={product?.platforms ?? []} kind={kind} baseline={baselinePolicy} disabled={disabled}
              onChange={(next) => onPolicyChange(JSON.stringify(next, null, 2))} />
              : <p className="px-4 py-3 text-xs text-danger">{t("licenseConfiguration.policyJsonInvalid", "Policy JSON is invalid. Open Raw JSON to correct it.")}</p>}
          </section>
        </div>}
      </fieldset>
    </div>
  </ConfigurationValidation.Provider>;
}

function JsonField({ id, label, value, onChange, tourId, invalid, message }: {
  id: string; label: string; value: string; onChange: (value: string) => void; tourId: string; invalid: boolean; message?: string;
}) {
  const { t } = useTranslation();
  return <div className="space-y-2" data-tour-id={tourId}>
    <Label htmlFor={id}>{label}</Label>
    <Textarea id={id} value={value} onChange={(event) => onChange(event.target.value)} rows={12} spellCheck={false}
      aria-invalid={invalid} aria-describedby={invalid ? `${id}-error` : undefined} />
    {invalid && <p id={`${id}-error`} role="alert" className="text-xs text-danger">{message ?? t("licenses.jsonValidationError", "Entitlements and policy must be valid JSON objects.")}</p>}
  </div>;
}

function parseObject(value: string): Record<string, unknown> | undefined {
  try { return parseJsonObject(value); } catch { return undefined; }
}
